import { useEffect, useState, useCallback } from 'react'
import { isIOS, isStandalone } from '../lib/pwaUtils'

const STORAGE_KEY = 'cc_push_prompt_state'
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000
const MAX_PROMPTS = 2

export interface PushPromptState {
  visitCount: number
  promptCount: 0 | 1 | 2
  lastPromptedAt: number | null
  outcome: null | 'enabled' | 'dismissed'
}

const DEFAULT_STATE: PushPromptState = {
  visitCount: 0,
  promptCount: 0,
  lastPromptedAt: null,
  outcome: null,
}

function readState(): PushPromptState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_STATE }
    const parsed = JSON.parse(raw) as Partial<PushPromptState>
    return {
      visitCount: typeof parsed.visitCount === 'number' ? parsed.visitCount : 0,
      promptCount:
        parsed.promptCount === 1 || parsed.promptCount === 2 ? parsed.promptCount : 0,
      lastPromptedAt:
        typeof parsed.lastPromptedAt === 'number' ? parsed.lastPromptedAt : null,
      outcome:
        parsed.outcome === 'enabled' || parsed.outcome === 'dismissed'
          ? parsed.outcome
          : null,
    }
  } catch {
    return { ...DEFAULT_STATE }
  }
}

function writeState(state: PushPromptState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // localStorage may be disabled (private mode, quota) — non-critical
  }
}

export interface UsePushPromptTriggerResult {
  shouldShow: boolean
  recordEnabled: () => void
  recordDismissed: () => void
}

export function usePushPromptTrigger(opts: {
  pushSupported: boolean
  isSubscribed: boolean
  permission: NotificationPermission | 'unsupported'
}): UsePushPromptTriggerResult {
  const { pushSupported, isSubscribed, permission } = opts

  const [state, setState] = useState<PushPromptState>(() => {
    if (typeof window === 'undefined') return { ...DEFAULT_STATE }
    const current = readState()
    const next = { ...current, visitCount: current.visitCount + 1 }
    writeState(next)
    return next
  })

  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    if (!pushSupported) {
      setShouldShow(false)
      return
    }
    if (typeof Notification === 'undefined') {
      setShouldShow(false)
      return
    }
    if (permission !== 'default') {
      setShouldShow(false)
      return
    }
    if (isSubscribed) {
      setShouldShow(false)
      return
    }
    if (state.outcome !== null) {
      setShouldShow(false)
      return
    }
    if (state.promptCount >= MAX_PROMPTS) {
      setShouldShow(false)
      return
    }
    if (isIOS() && !isStandalone()) {
      setShouldShow(false)
      return
    }

    if (state.promptCount > 0 && state.lastPromptedAt !== null) {
      const elapsed = Date.now() - state.lastPromptedAt
      if (elapsed < COOLDOWN_MS) {
        setShouldShow(false)
        return
      }
    }

    const installTrigger = isStandalone()
    const returnTrigger = state.visitCount >= 2

    setShouldShow(installTrigger || returnTrigger)
  }, [
    pushSupported,
    isSubscribed,
    permission,
    state.outcome,
    state.promptCount,
    state.lastPromptedAt,
    state.visitCount,
  ])

  const recordEnabled = useCallback(() => {
    setState((prev) => {
      const next: PushPromptState = {
        ...prev,
        outcome: 'enabled',
        lastPromptedAt: Date.now(),
      }
      writeState(next)
      return next
    })
    setShouldShow(false)
  }, [])

  const recordDismissed = useCallback(() => {
    setState((prev) => {
      const newCount = Math.min(prev.promptCount + 1, MAX_PROMPTS) as 0 | 1 | 2
      const next: PushPromptState = {
        ...prev,
        promptCount: newCount,
        lastPromptedAt: Date.now(),
        outcome: newCount >= MAX_PROMPTS ? 'dismissed' : null,
      }
      writeState(next)
      return next
    })
    setShouldShow(false)
  }, [])

  return { shouldShow, recordEnabled, recordDismissed }
}
